import { observer } from 'mobx-react-lite'
import { Button, Divider, Tooltip, Typography } from 'shared/ui'
import { makeTextFieldElement, TextFieldPlaceholder } from 'shared/ui/TextField'
import { useCopyToClipboard } from 'shared/hooks/useCopyToClipboard'
import type { IResponseClickToText } from 'entities/ClickToText/api/type'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { downloadQRCodeAsPNG, downloadQRCodeAsSVG } from 'pages/clickToText/lib'
import styles from './styles.module.scss'

export const ClickToTextModalContent = observer(
  ({ short_url, qr_code, id }: IResponseClickToText) => {
    const { onCopy, copied } = useCopyToClipboard(short_url, {
      showToast: true,
      toastTitle: 'Link copied',
      toastType: 'success',
    })
    const entities = useEntitiesContext()
    const { eventLogStore, organizationStore } = entities

    const handleDownload = (format: 'SVG' | 'PNG') => {
      eventLogStore.logEvent(
        'Click to Text Used',
        {
          event_id: 'click_to_text_used',
          action: 'Download clicked',
          linkId: id,
          agencyId: organizationStore.agency_id,
        },
        { groupId: organizationStore.id }
      )
      if (format === 'SVG') {
        downloadQRCodeAsSVG(qr_code)
      } else {
        downloadQRCodeAsPNG(qr_code)
      }
    }

    return (
      <div className={styles.content}>
        <Typography variant={'heading-md'} ariaLabel={'Details Title'} marginBottom={8}>
          Your text link is ready
        </Typography>
        <Typography
          variant={'body-rg-regular'}
          ariaLabel={'Details Description'}
          className={styles.description}
        >
          Share this QR code or link so people can text you instantly.
        </Typography>
        <div className={styles.image}>
          <img src={qr_code} alt='QR Code' className={styles.qrImage} />
        </div>
        <div className={styles.actions}>
          <Button
            fullWidth
            text='PNG'
            iconProps={{ icon: 'download' }}
            size='medium'
            onClick={() => handleDownload('PNG')}
          />
          <Button
            fullWidth
            text='SVG'
            iconProps={{ icon: 'download' }}
            size='medium'
            onClick={() => handleDownload('SVG')}
          />
        </div>
        <Divider className={styles.divider} sx={{ marginBottom: '16px', marginTop: '16px' }}>
          <Typography className={styles.dividerText} ariaLabel={'Divider Text'}>
            or copy link
          </Typography>
        </Divider>
        <div className={styles.fieldContent}>
          <TextFieldPlaceholder
            className={styles.linkField}
            size='medium'
            variant='stroke'
            placeholder={
              <Tooltip placement='top' label={short_url} fullWidth>
                <Typography
                  tag='div'
                  ariaLabel='Link_Url'
                  variant='text-input-field'
                  color='var(--content-primary-primary)'
                  ellipsis
                >
                  {short_url}
                </Typography>
              </Tooltip>
            }
            rightActions={[
              makeTextFieldElement({
                element: (
                  <Button
                    icon={copied ? undefined : 'copy'}
                    text={copied ? 'Copied' : 'Copy'}
                    typeBtn='contained'
                    contained='tertiary'
                    size='small'
                    onClick={onCopy}
                    tag='div'
                  />
                ),
              }),
            ]}
          />
        </div>
      </div>
    )
  }
)
